import {
    PDF_BAO_CAO_MARGINS,
    PdfPageLayoutOptions,
} from "@/components/shared/pdf-viewer/pdf-content.config";
import ReportType from "@/core/enums/ReportTypes.enums";

/** Sheet keys for Theo dõi VTPT thu hồi PDF viewer. */
export type ThuHoiVtptSheet = "b01_a" | "b01_b" | "b02_a" | "b02_b";

export const THU_HOI_VTPT_SHEETS: readonly ThuHoiVtptSheet[] = [
  "b01_a",
  "b01_b",
  "b02_a",
  "b02_b",
] as const;

export const THU_HOI_VTPT_SHEET_TITLES: Record<ThuHoiVtptSheet, string> = {
  b01_b: "B01.b — Tổng hợp tái sử dụng VTPT thu hồi",
  b02_b: "B02.b — Tổng hợp thanh lý VTPT thu hồi",
  b01_a: "B01.a — Chi tiết tái sử dụng VTPT thu hồi",
  b02_a: "B02.a — Chi tiết thanh lý VTPT thu hồi",
};

export function isThuHoiVtptSheet(value: unknown): value is ThuHoiVtptSheet {
  return (
    typeof value === "string" &&
    (THU_HOI_VTPT_SHEETS as readonly string[]).includes(value)
  );
}

export interface BaoCaoPdfParams {
  reportType: ReportType;
  from?: string;
  to?: string;
  khoId?: number;
  matk?: string;
  mact?: string;
  tenDoiTac?: string;
  capSuaChuaId?: number;
  capSuaChuaTen?: string;
  toaXeId?: number;
  toaXeTen?: string;
  toaXeMa?: string;
  idHopDong?: number;
  month?: number;
  year?: number;
  idDmVatTu?: number;
  nhomVatTu?: string;
  idCapSc?: number;
  capSuaChuaLabel?: string;
  isTongHopNhap?: boolean;
  hideZero?: boolean;
  hideZeroActivity?: boolean;
  page?: number;
  perPage?: number;
  search?: string;
  /** Theo dõi VTPT thu hồi: which sheet to render in PDF. */
  sheet?: ThuHoiVtptSheet;
  /** Comma-separated vật tư ids for chi tiết sheets (B01.a / B02.a). */
  vatTuIds?: string;
}

export const BAO_CAO_VIEWER_PER_PAGE_OPTIONS = [50, 100, 200] as const;

export const BAO_CAO_VIEWER_DEFAULT_PER_PAGE =
  BAO_CAO_VIEWER_PER_PAGE_OPTIONS[0];

export function resolveBaoCaoViewerPerPage(perPage?: number): number {
  if (
    perPage &&
    BAO_CAO_VIEWER_PER_PAGE_OPTIONS.includes(
      perPage as (typeof BAO_CAO_VIEWER_PER_PAGE_OPTIONS)[number]
    )
  ) {
    return perPage;
  }

  return BAO_CAO_VIEWER_DEFAULT_PER_PAGE;
}

const QUERY_KEYS: (keyof BaoCaoPdfParams)[] = [
  "from",
  "to",
  "khoId",
  "matk",
  "mact",
  "tenDoiTac",
  "capSuaChuaId",
  "capSuaChuaTen",
  "toaXeId",
  "toaXeTen",
  "toaXeMa",
  "idHopDong",
  "month",
  "year",
  "idDmVatTu",
  "nhomVatTu",
  "idCapSc",
  "capSuaChuaLabel",
  "page",
  "perPage",
  "search",
  "hideZeroActivity",
  "sheet",
  "vatTuIds",
];

const NUMBER_KEYS = new Set<keyof BaoCaoPdfParams>([
  "khoId",
  "capSuaChuaId",
  "toaXeId",
  "idHopDong",
  "month",
  "year",
  "idDmVatTu",
  "idCapSc",
  "page",
  "perPage",
]);

const BOOLEAN_KEYS = new Set<keyof BaoCaoPdfParams>([
  "isTongHopNhap",
  "hideZero",
  "hideZeroActivity",
]);

export function serializeBaoCaoPdfParams(
  params: BaoCaoPdfParams
): Record<string, string> {
  const query: Record<string, string> = {};

  QUERY_KEYS.forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    query[key] = String(value);
  });

  if (params.isTongHopNhap !== undefined) {
    query.isTongHopNhap = params.isTongHopNhap ? "1" : "0";
  }

  if (params.hideZero) {
    query.hideZero = "1";
  }

  if (params.hideZeroActivity) {
    query.hideZeroActivity = "1";
  }

  return query;
}

export function parseBaoCaoPdfParams(
  reportType: string,
  query: Record<string, string | string[] | undefined | null>
): BaoCaoPdfParams | null {
  if (!Object.values(ReportType).includes(reportType as ReportType)) {
    return null;
  }

  const params: BaoCaoPdfParams = {
    reportType: reportType as ReportType,
  };

  QUERY_KEYS.forEach((key) => {
    const raw = query[key];
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value === undefined || value === null || value === "") return;

    if (NUMBER_KEYS.has(key)) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        (params as unknown as Record<string, unknown>)[key] = parsed;
      }
      return;
    }

    (params as unknown as Record<string, unknown>)[key] = value;
  });

  const isTongHopNhapRaw = query.isTongHopNhap;
  const isTongHopNhapValue = Array.isArray(isTongHopNhapRaw)
    ? isTongHopNhapRaw[0]
    : isTongHopNhapRaw;

  if (isTongHopNhapValue !== undefined && isTongHopNhapValue !== null) {
    params.isTongHopNhap =
      isTongHopNhapValue === "1" || isTongHopNhapValue === "true";
  }

  const hideZeroRaw = query.hideZero;
  const hideZeroValue = Array.isArray(hideZeroRaw)
    ? hideZeroRaw[0]
    : hideZeroRaw;

  if (hideZeroValue !== undefined && hideZeroValue !== null) {
    params.hideZero = hideZeroValue === "1" || hideZeroValue === "true";
  }

  const hideZeroActivityRaw = query.hideZeroActivity;
  const hideZeroActivityValue = Array.isArray(hideZeroActivityRaw)
    ? hideZeroActivityRaw[0]
    : hideZeroActivityRaw;

  if (hideZeroActivityValue !== undefined && hideZeroActivityValue !== null) {
    params.hideZeroActivity =
      hideZeroActivityValue === "1" || hideZeroActivityValue === "true";
  }

  if (BOOLEAN_KEYS.has("isTongHopNhap") && params.isTongHopNhap === undefined) {
    if (params.reportType === ReportType.TONG_HOP_NHAP_VAT_TU) {
      params.isTongHopNhap = true;
    } else if (params.reportType === ReportType.TONG_HOP_XUAT_VAT_TU) {
      params.isTongHopNhap = false;
    }
  }

  if (
    params.reportType === ReportType.NHAP_XUAT_TON_KHO &&
    params.hideZeroActivity === undefined
  ) {
    params.hideZeroActivity = true;
  }

  if (params.sheet && !isThuHoiVtptSheet(params.sheet)) {
    delete params.sheet;
  }

  return params;
}

export function parseVatTuIdsQuery(value?: string): number[] {
  if (!value) return [];

  return value
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function formatVatTuIdsQuery(ids: number[]): string {
  return ids.filter((id) => Number.isFinite(id) && id > 0).join(",");
}

export function getBaoCaoReportTitle(
  reportType: string,
  sheet?: string
): string {
  if (
    reportType === ReportType.THEO_DOI_VTPT_THU_HOI &&
    isThuHoiVtptSheet(sheet)
  ) {
    return THU_HOI_VTPT_SHEET_TITLES[sheet];
  }

  return BAO_CAO_REPORT_TITLES[reportType as ReportType] ?? "Báo cáo";
}

export function parseMonthDate(
  value: string | undefined,
  fallbackYear?: number
): Date | null {
  if (!value) return null;

  const monthMatch = /^(\d{4})-(\d{2})$/.exec(value);
  if (monthMatch) {
    return new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1);
  }

  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateMatch) {
    return new Date(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3])
    );
  }

  if (fallbackYear) {
    const monthOnly = Number(value);
    if (!Number.isNaN(monthOnly) && monthOnly >= 1 && monthOnly <= 12) {
      return new Date(fallbackYear, monthOnly - 1, 1);
    }
  }

  return null;
}

export function formatMonthQuery(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${date.getFullYear()}-${month}`;
}

export function formatDateQuery(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function getBaoCaoPdfLayoutOptions(
  reportType: string
): PdfPageLayoutOptions {
  if (
    reportType === ReportType.SO_CAN_DOI_VAT_TU ||
    reportType === ReportType.CHI_TIET_XUAT_THEO_DOI_TUONG ||
    reportType === ReportType.BANG_KE_CHI_TIET_NHAP ||
    reportType === ReportType.BANG_KE_CHI_TIET_XUAT ||
    reportType === ReportType.THEO_DOI_THUC_HIEN_HOP_DONG ||
    reportType === ReportType.NHAP_XUAT_TON_KHO ||
    reportType === ReportType.NHAP_KHO_VAT_TU_PHU_TUNG ||
    reportType === ReportType.GIA_THANH_SUA_CHUA_CAC_CAP ||
    reportType === ReportType.THEO_DOI_VTPT_THU_HOI
  ) {
    return {
      orientation: "landscape",
      margins: PDF_BAO_CAO_MARGINS,
    };
  }

  return {
    orientation: "portrait",
    margins: PDF_BAO_CAO_MARGINS,
  };
}

export const BAO_CAO_REPORT_TITLES: Partial<Record<ReportType, string>> = {
  [ReportType.SO_CAN_DOI_VAT_TU]: "Sổ cân đối hàng tồn kho",
  [ReportType.BANG_KE_CHI_TIET_NHAP]: "Bảng kê chi tiết nhập",
  [ReportType.BANG_KE_CHI_TIET_XUAT]: "Bảng kê chi tiết xuất",
  [ReportType.TONG_HOP_NHAP_VAT_TU]: "Tổng hợp nhập vật tư",
  [ReportType.TONG_HOP_XUAT_VAT_TU]: "Tổng hợp xuất vật tư",
  [ReportType.CHI_TIET_XUAT_THEO_DOI_TUONG]:
    "Bảng kê chi tiết xuất theo đối tượng",
  [ReportType.THEO_DOI_THUC_HIEN_HOP_DONG]: "Theo dõi thực hiện hợp đồng",
  [ReportType.THEO_DOI_VTPT_THU_HOI]: "Theo dõi VTPT thu hồi",
  [ReportType.NHAP_XUAT_TON_KHO]: "Tình hình nhập - xuất tồn kho",
  [ReportType.NHAP_KHO_VAT_TU_PHU_TUNG]: "Tình hình nhập kho phụ tùng",
  [ReportType.GIA_THANH_SUA_CHUA_CAC_CAP]: "Giá thành sửa chữa các cấp",
};
